// This file contains domain model type "Hierarchy" helper functions.

import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { HierarchicalEntity } from "@iventis/domain-model/model/hierarchicalEntity";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findHierarchyEntityById = <T extends HierarchicalEntity>(arr: Hierarchy<T>[], id: string): Hierarchy<T> => {
    if (arr?.length > 0) {
        return (
            arr.find((hierarchy) => hierarchy?.item?.id === id) ||
            findHierarchyEntityById(
                arr.flatMap((hierarchy) => hierarchy?.children || []),
                id
            )
        );
    }
    return null;
};

export const aggregateHierarchy = async <T extends HierarchicalEntity>(hierarchy1: Hierarchy<T>, hierarchy2: Hierarchy<T>) => {
    if (hierarchy1 == null) {
        return hierarchy2;
    }
    if (hierarchy1.item.id !== hierarchy2.item.id) {
        throw new Error("It would not be sensible to aggregate two different hierarchies, since they should be identical with pagination on the children");
    }
    return {
        item: hierarchy1.item,
        children: [...hierarchy1.children, ...hierarchy2.children],
    } as Hierarchy<T>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findHierarchyEntityByType = <T extends HierarchicalEntity>(arr: Hierarchy<T>[], typeId: string, typeIdAccessor: (item: T) => string): Hierarchy<T> => {
    if (arr?.length > 0) {
        return (
            arr.find((hierarchy) => typeIdAccessor(hierarchy?.item) === typeId) ||
            findHierarchyEntityByType<T>(
                arr.flatMap((hierarchy: Hierarchy<T>) => hierarchy?.children || []),
                typeId,
                typeIdAccessor
            )
        );
    }
    return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findHierarchyEntitiesByType = <T extends HierarchicalEntity>(arr: Hierarchy<T>[], typeId: string, typeIdAccessor: (item: T) => string): Hierarchy<T>[] => {
    if (arr?.length > 0) {
        const filtered = arr.filter((hierarchy: Hierarchy<T>) => typeIdAccessor(hierarchy?.item) === typeId);
        return [
            ...filtered,
            ...(findHierarchyEntitiesByType<T>(
                arr.flatMap((hierarchy: Hierarchy<T>) => hierarchy?.children || []),
                typeId,
                typeIdAccessor
            ) as Hierarchy<T>[]),
        ];
    }
    return [];
};

export const findHierarchyEntitiesByIds = <T extends HierarchicalEntity>(arr: Hierarchy<T>[], ids: string[]): Hierarchy<T>[] => {
    if (arr?.length > 0) {
        const filtered = arr.filter((hierarchy: Hierarchy<T>) => ids.includes(hierarchy.item.id));
        return [
            ...filtered,
            ...(findHierarchyEntitiesByIds<T>(
                arr.flatMap((hierarchy: Hierarchy<T>) => hierarchy?.children || []),
                ids
            ) as Hierarchy<T>[]),
        ];
    }
    return [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findEventBlockHierarchyEntitiesByCalendarLevel = (arr: Hierarchy<EventBlock>[], calendarLevel: number): Hierarchy<EventBlock>[] => {
    if (arr?.length > 0) {
        // eslint-disable-next-line
        const filtered = arr.filter((hierarchy: Hierarchy<EventBlock>) => hierarchy?.item?.type.calendarLevel == calendarLevel);
        return filtered.length > 0
            ? filtered
            : findEventBlockHierarchyEntitiesByCalendarLevel(
                  arr.flatMap((hierarchy: Hierarchy<EventBlock>) => hierarchy?.children || []),
                  calendarLevel
              );
    }
    return [];
};

// Get the entity and its parents as an array from hierarchy
export const getParentHierarchyTreeByIdAsArray = <T extends HierarchicalEntity>(hierarchy: Hierarchy<T>[], id: string): T[] =>
    hierarchyToArray(getParentHierarchyTreeById(hierarchy, id));

// Pass in the Id of the entity you wish to find.
// Returns the entity within all its parents from the tree.
const getParentHierarchyTreeById = <T extends HierarchicalEntity>(hierarchy: Hierarchy<T>[], id: string, hierarchyPathFound: Hierarchy<T> = null): Hierarchy<T> => {
    const foundHierarchyEntity: Hierarchy<T> = findHierarchyEntityById(hierarchy, id);
    let newHierarchyPath = null;
    if (foundHierarchyEntity) {
        newHierarchyPath = { item: foundHierarchyEntity.item, children: hierarchyPathFound == null ? [] : [hierarchyPathFound] };
        return getParentHierarchyTreeById(hierarchy, foundHierarchyEntity.item.parentId, newHierarchyPath);
    }
    return hierarchyPathFound;
};

// Hierarchy to flat array
export const hierarchyToArray = <T extends HierarchicalEntity>(hierarchy: Hierarchy<T>): T[] => {
    const hierarchyArray: T[] = [];
    const hierarchyToArrayRecusrive = (hierarchy: Hierarchy<T>) => {
        if (hierarchy?.item != null) {
            hierarchyArray.push(hierarchy.item);
        }
        hierarchy.children?.forEach((childHierarchy) => hierarchyToArrayRecusrive(childHierarchy));
    };
    hierarchyToArrayRecusrive(hierarchy);
    return hierarchyArray;
};

export const findAncestorInHierarchy = <T extends HierarchicalEntity>(hierarchy: Hierarchy<T>, id: string, levelsUp: number): Hierarchy<T> => {
    let ancestor: Hierarchy<T>;
    let currentNumber = 0;
    const findParent = (_tree, _id) => {
        const child = findHierarchyEntityById([_tree], _id);
        const parent = child && findHierarchyEntityById<T>([_tree], child.item.parentId);
        if (parent && currentNumber < levelsUp) {
            ancestor = parent;
            findParent(_tree, parent.item.id);
            currentNumber += 1;
        }
    };
    findParent(hierarchy, id);
    return ancestor;
};

/**
 * A function that runs a given callback on each entity down the hierarchy tree (except the top entity).
 * @param topLevelHierarchy Top level hierarchical entity whose children you wish to apply the callback to
 * @param callback A callback function you wish to apply to the entities in the hierarchy
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const loopHierarchy = <T extends HierarchicalEntity>(topLevelHierarchy: Hierarchy<T>, callback: (hierarchy: Hierarchy<T>) => void): void => {
    const evaluateHierarchy = (hierarchy: Hierarchy<T>) => {
        hierarchy.children.forEach((h) => {
            callback(h as Hierarchy<T>);
            evaluateHierarchy(h as Hierarchy<T>);
        });
    };
    evaluateHierarchy(topLevelHierarchy);
};
